import { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { Checkbox, Divider, MenuItem, Stack, Typography } from '@mui/material';
import { IllustrationSearchError, UserAvatar } from '@vs-online-only-app/ui';
import { TransferFormFieldValues } from './SelectedConversationTransferModal';
import { TransferContext } from './TransferContext';
import AssociatesListLoader from './AssociatesListLoader';

export const Container = styled(Stack)`
  background: ${({ theme }) => theme.palette.background.default};
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2}px;
  padding: ${({ theme }) => theme.spacing(2, 0)};
  gap: ${({ theme }) => theme.spacing(1)};
`;

const PlaceholderContainer = styled(Stack)`
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(0, 2)};
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const SummaryContainer = styled(Stack)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(0, 2)};
`;

const StyledMenuItem = styled(MenuItem)`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  position: relative;
  padding: ${({ theme }) => theme.spacing(1, 2)};

  &:after {
    content: '';
    position: absolute;
    height: 1px;
    background: ${({ theme }) => theme.palette.divider};
    width: calc(100% - ${({ theme }) => theme.spacing(10.5)});
    top: -2px;
    right: ${({ theme }) => theme.spacing(3.5)};
  }
`;

export const AssociatesList: FC = () => {
  const { filteredAssociates, isLoading } = useContext(TransferContext);
  const {
    setValue: setFieldValue,
    clearErrors: clearFieldError,
    formState: { errors: formErrors },
    watch,
  } = useFormContext<TransferFormFieldValues>();

  const selectedAssociateIds = watch('selectedAssociateIds');

  useEffect(() => {
    if (formErrors?.root && selectedAssociateIds.length) {
      setFieldValue('selectedAssociateIds', []);
    }
  }, [formErrors?.root, selectedAssociateIds.length, setFieldValue]);

  const selectorOptions = useMemo(
    () =>
      filteredAssociates
        .map((associate) => {
          const departmentsCount = associate.departments.length;
          let descriptionPostfix = '';

          if (departmentsCount > 1) {
            descriptionPostfix = `+ ${departmentsCount - 1} more`;
          }

          return {
            value: associate.id,
            isChecked: selectedAssociateIds.includes(associate.id),
            label: `${associate.firstName} ${associate.lastName}`,
            description: `${associate.departments[0].name} ${descriptionPostfix}`,
            imageSrc: associate.image,
          };
        })
        .sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          if (a.label > b.label) {
            return 1;
          }
          return 0;
        }) || [],
    [filteredAssociates, selectedAssociateIds],
  );

  const hasAllAssociatesSelected = useMemo(
    () => selectedAssociateIds.length === filteredAssociates?.length,
    [selectedAssociateIds, filteredAssociates],
  );
  const hasSomeAssociatesSelected = useMemo(
    () => selectedAssociateIds.length > 0,
    [selectedAssociateIds],
  );

  const selectAllAssociates = useCallback(
    () =>
      setFieldValue(
        'selectedAssociateIds',
        filteredAssociates.map((associate) => associate.id) || [],
      ),
    [filteredAssociates, setFieldValue],
  );

  const deselectAllAssociates = () => setFieldValue('selectedAssociateIds', []);

  const selectAssociate = (associateId: string) => {
    setFieldValue(
      'selectedAssociateIds',
      selectedAssociateIds.includes(associateId)
        ? selectedAssociateIds.filter((id) => id !== associateId)
        : selectedAssociateIds.concat(associateId),
    );
    if (formErrors?.root) {
      clearFieldError('root');
    }
    if (formErrors?.selectedAssociateIds) {
      clearFieldError('selectedAssociateIds');
    }
  };

  if (isLoading) {
    return <AssociatesListLoader />;
  }

  if (!filteredAssociates.length) {
    return (
      <Container>
        <PlaceholderContainer>
          <IllustrationSearchError />
          <Typography align="center" variant="body2" color="textSecondary">
            No one is online at the moment.
          </Typography>
        </PlaceholderContainer>
      </Container>
    );
  }

  return (
    <Container>
      <SummaryContainer>
        <Typography variant="body2" color="textSecondary">
          <strong>{selectedAssociateIds.length}</strong> selected
        </Typography>
        <Checkbox
          checked={hasAllAssociatesSelected}
          indeterminate={hasSomeAssociatesSelected && !hasAllAssociatesSelected}
          onChange={() => {
            if (hasSomeAssociatesSelected) {
              deselectAllAssociates();
            } else {
              selectAllAssociates();
            }
          }}
        />
      </SummaryContainer>
      <Divider />
      <Stack maxHeight="260px" overflow="hidden auto" gap={0.5} pb={1}>
        {selectorOptions.map(
          ({ value, label, imageSrc, isChecked, description }) => (
            <StyledMenuItem onClick={() => selectAssociate(value)} key={value}>
              <UserAvatar imageSrc={imageSrc} />
              <Stack>
                <Typography variant="body2" fontWeight="bold">
                  {label}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {description}
                </Typography>
              </Stack>
              <Checkbox checked={isChecked} />
            </StyledMenuItem>
          ),
        )}
      </Stack>
    </Container>
  );
};
